"use client"

import { useState, useMemo, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { UserPlus, Users, X, Trash2, Send, FileText, Clipboard, Edit3, Plus, Download, FileDown } from "lucide-react"
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ResponseViewerModal from '../components/ResponseViewerModal';
import axios from "axios"
import config from "../config"
import AddGuestModal from "./AddGuestModal"
import SeatAssignmentModal from "./SeatAssignmentModal"
import ConfirmDialog from "../components/ConfirmDialog"
import GuestListTable from "../components/GuestListTable"
import { Button, Typography, CircularProgress, Snackbar, Alert } from "@mui/material"
import SimpleFormBuilder from "../components/forms/SimpleFormBuilder"

// jsPDF and autoTable will be initialized inside the handleExportResponses function

const GuestListTab = ({
  event = {},
  invitations = [],
  guests = [],
  isCreator = false,
  user = {},
  isLoggedIn = false,
  fetchInvitationsAndGuests = () => { },
  showSuccessModal: propShowSuccessModal = () => { },
  showFailureModal: propShowFailureModal = () => { }
}) => {
  // State for data with proper initialization
  const [invitationsState, setInvitations] = useState([])
  const [guestsState, setGuests] = useState([])
  const [userHasInvitation, setUserHasInvitation] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState({
    invitations: { count: 0, next: null, previous: null },
    guests: { count: 0, next: null, previous: null },
  })

  // State for UI and modals
  const [showAddGuestForm, setShowAddGuestForm] = useState(false)
  const [showSeatAssignment, setShowSeatAssignment] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSendFormModal, setShowSendFormModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditingSeat, setIsEditingSeat] = useState(false)
  const [formToDelete, setFormToDelete] = useState(null)

  // State for data and selections
  const [invitationSearch, setInvitationSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [invitationStatus, setInvitationStatus] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [invitationPage, setInvitationPage] = useState(1)
  const [selectedInvitation, setSelectedInvitation] = useState(null)
  const [invitationToDelete, setInvitationToDelete] = useState(null)
  const [selectedFormType, setSelectedFormType] = useState("rsvp")
  const [seatNumber, setSeatNumber] = useState("")
  const [selectedGuestId, setSelectedGuestId] = useState(null)
  const [selectedEventId, setSelectedEventId] = useState(null)
  const [isSeatModalOpen, setIsSeatModalOpen] = useState(false)
  const [showSeatModal, setShowSeatModal] = useState(false)
  const [selectedGuest, setSelectedGuest] = useState(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [availableForms, setAvailableForms] = useState([])
  const [showFormBuilder, setShowFormBuilder] = useState(false)
  const [selectedFormId, setSelectedFormId] = useState("")
  const [selectedForm, setSelectedForm] = useState(null)
  const [forms, setForms] = useState([])
  const [showDeleteFormConfirm, setShowDeleteFormConfirm] = useState(false)
  const [showViewFormModal, setShowViewFormModal] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [formResponseUrl, setFormResponseUrl] = useState("")
  const [formStatus, setFormStatus] = useState({})
  const [formSubmissions, setFormSubmissions] = useState({})
  const [successModal, setSuccessModal] = useState({ open: false, message: "" })
  const [failureModal, setFailureModal] = useState({ open: false, message: "" })
  const [isBulkAction, setIsBulkAction] = useState(false)
  const [selectedInvitations, setSelectedInvitations] = useState({})
  const [isSendingForm, setIsSendingForm] = useState(false)
  const [showLinkCopied, setShowLinkCopied] = useState(false)
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [responseCount, setResponseCount] = useState(0)
  const [formResponses, setFormResponses] = useState([])
  const [isLoadingResponses, setIsLoadingResponses] = useState(false)
  const [isLoadingResponse, setIsLoadingResponse] = useState(false)
  const [viewingResponse, setViewingResponse] = useState(null)
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false)

  const showSuccessModal = (message) => {
    setSuccessModal({ open: true, message })
    setLoading(false)
    setIsSendingForm(false)
    propShowSuccessModal(message)

    // Clear any existing error messages
    setError(null)
  }

  const showFailureModal = (message) => {
    setFailureModal({ open: true, message })
    setError(message)
    setLoading(false)
    setIsSendingForm(false)
    propShowFailureModal(message)
  }

  const handleCloseSuccessModal = () => {
    setSuccessModal((prev) => ({ ...prev, open: false }))
  }

  const handleCloseFailureModal = () => {
    setFailureModal((prev) => ({ ...prev, open: false }))
  }

  const navigate = useNavigate()

  // Handle form builder
  const openFormBuilder = (formId = null) => {
    setSelectedFormId(formId || "")
    setShowFormBuilder(true)
  }

  // Handle manage forms
  const handleManageForms = () => {
    // Open a modal or navigate to form management
    // For now, let's open the send form modal which has view/edit options
    setShowSendFormModal(true)
  }

  // Handle view form
  const handleViewForm = (formId) => {
    const form = forms.find(f => f.id.toString() === formId.toString());
    if (form) {
      setSelectedForm(form);
      setShowViewFormModal(true);
    }
  }

  // Handle edit form
  const handleEditForm = (formId) => {
    const form = forms.find(f => f.id.toString() === formId.toString());
    if (form) {
      // console.log('🔍 Editing form:', formId, form);
      setSelectedFormId(formId);
      setShowFormBuilder(true);
      setShowSendFormModal(false);
    }
  }

  // Handle form deletion
  const handleDeleteForm = async () => {
    if (!formToDelete) return

    setIsDeleting(true)
    try {
      const token = localStorage.getItem("access_token")
      const baseUrl = config.API_BASE_URL.replace(/\/+$/, "")
      const url = `${baseUrl}/forms/event-forms/${formToDelete}/`

      await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })

      // Refresh forms list
      const formsResponse = await axios.get(`${baseUrl}/forms/event-forms/?event_id=${event.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })

      setForms(formsResponse.data)
      setSelectedFormId("")
      setSuccessMessage("Form deleted successfully!")
      showSuccessModal("Form deleted successfully!")

      // Refresh the forms list in the parent component if needed
      if (fetchInvitationsAndGuests) {
        fetchInvitationsAndGuests()
      }
    } catch (error) {
      // console.error("Error deleting form:", error)
      const errorMessage = "Failed to delete form. " + (error.response?.data?.detail || error.message || "")
      setError(errorMessage)
      showFailureModal(errorMessage)
    } finally {
      setIsDeleting(false)
      setFormToDelete(null)
      setShowDeleteFormConfirm(false)
    }
  }

  // Handle successful form creation
  const handleFormCreated = (formData) => {
    showSuccessModal("Form saved successfully!")
    // Set the newly created form as selected
    setSelectedFormId(formData.id)
    // Refresh the forms list to include the new form
    fetchForms().then(() => {
      // After forms are refreshed, reopen the send form modal
      // but don't send the form automatically
      setShowSendFormModal(true)
    })
  }

  // Handle form deletion confirmation - alias for handleDeleteForm for backward compatibility
  const confirmDeleteForm = handleDeleteForm

  // Function to fetch available forms along with their responses and question responses
  const fetchForms = async () => {
    if (!event?.id) return

    try {
      setLoading(true)
      const token = localStorage.getItem("access_token")
      if (!token) {
        // console.error("No access token found")
        setLoading(false)
        return
      }

      const baseUrl = config.API_BASE_URL.replace(/\/+$/, "")

      // First, fetch the forms with their responses
      const formsUrl = `${baseUrl}/forms/event-forms/?event_id=${event.id}`
      // console.log("Fetching forms from:", formsUrl)

      const formsResponse = await axios.get(formsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })

      // console.log("Forms response:", formsResponse.data)

      // If no forms, set empty array and return
      if (!formsResponse.data ||
        (Array.isArray(formsResponse.data) && formsResponse.data.length === 0) ||
        (formsResponse.data.results && formsResponse.data.results.length === 0)) {
        // console.log("No forms found for this event")
        setForms([])
        setAvailableForms([])
        setLoading(false)
        return
      }

      // Process forms from the response
      let formsData = Array.isArray(formsResponse.data)
        ? formsResponse.data
        : (formsResponse.data.results || formsResponse.data || []);

      // console.log("Processing forms data:", formsData);

      // Process each form to format the responses correctly
      const processedForms = formsData.map(form => {
        // The responses are already included in the form data
        const responses = form.responses || [];
        // console.log(`Form ${form.id} has ${responses.length} responses:`, responses);

        return {
          ...form,
          responses: responses.map(response => ({
            ...response,
            // Ensure responses is always an array
            responses: Array.isArray(response.responses) ? response.responses : [],
            is_complete: true
          })),
          response_count: responses.length
        };
      });

      // console.log("Processed forms with responses:", processedForms);
      return processedForms;

      // Update state with processed forms
      setAvailableForms(processedForms);
      setForms(processedForms);

      // If we have forms but no selection, select the first one with responses
      if (processedForms.length > 0) {
        const firstFormWithResponses = processedForms.find(f => f.response_count > 0) || processedForms[0];
        if (firstFormWithResponses && !selectedFormId) {
          setSelectedFormId(firstFormWithResponses.id.toString());
          // console.log('Auto-selected form:', firstFormWithResponses.id, 'with', firstFormWithResponses.response_count, 'responses');
        }
      }
    } catch (error) {
      // console.error("Error fetching forms:", error)
      // console.error("Error details:", {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // })
      // Don't show error if it's just 404 (no forms exist yet)
      if (error.response?.status !== 404) {
        showFailureModal("Failed to load forms. Please try again later.")
      }
      setAvailableForms([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch a specific form response by submission ID
  const fetchFormResponse = async (submissionId) => {
    if (!submissionId) {
      showFailureModal('No form submission ID provided');
      return null;
    }

    try {
      setIsLoadingResponse(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${config.API_BASE_URL}/forms/submissions/${submissionId}/`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      // console.error('Error fetching form response:', error);
      showFailureModal('Failed to load form response. Please try again.');
      return null;
    } finally {
      setIsLoadingResponse(false);
    }
  };

  // Fetch form response by email if submission ID is not available
  const fetchResponseByEmail = async (email) => {
    if (!email || !selectedFormId) return null;

    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/forms/submissions/?form_id=${selectedFormId}&user_email=${encodeURIComponent(email)}`,
        { withCredentials: true }
      );

      return response.data?.results?.[0] || null;
    } catch (error) {
      // console.error('Error fetching response by email:', error);
      return null;
    }
  };

  // Handle view response button click
  const handleViewResponse = async (invitation) => {
    // console.log('Viewing response for invitation:', invitation);

    try {
      let response;
      const email = invitation.recipient_email || invitation.guest?.email;

      // Try to get submission ID from various possible locations
      const submissionId = invitation.form_submission_id ||
        invitation.form_submission?.id ||
        (invitation.form_responses && invitation.form_responses[0]?.id);

      if (submissionId) {
        // console.log('Fetching form response with ID:', submissionId);
        response = await fetchFormResponse(submissionId);
      } else if (email && invitation.form_status === 'completed') {
        // console.log('No submission ID found, trying to fetch by email:', email);
        response = await fetchResponseByEmail(email);

        if (!response) {
          throw new Error('No form submission found for this email');
        }
      } else {
        throw new Error('No form submission found for this invitation');
      }

      if (response) {
        // console.log('Form response received:', response);
        setViewingResponse({
          ...response,
          user_email: invitation.recipient_email || invitation.recipient?.email || 'Unknown',
          submitted_at: response.submitted_at || new Date().toISOString(),
        });
        setIsResponseModalOpen(true);
      } else {
        throw new Error('Received empty response from server');
      }
    } catch (error) {
      // console.error('Error viewing response:', error);
      showFailureModal(`Failed to load form response: ${error.message || 'Please try again.'}`);
    }
  };

  // Close response viewer
  const handleCloseResponseViewer = () => {
    setIsResponseModalOpen(false);
    setViewingResponse(null);
  };

  // Fetch form responses - only for event creators
  const fetchData = async () => {
    if (!event?.id) return;

    // Skip form fetching if user is not the event creator
    if (!isCreator) {
      setForms([]);
      return;
    }

    setIsLoadingResponses(true);
    try {
      // Check if forms exist before trying to fetch
      try {
        const formsCheck = await axios.head(
          `${config.API_BASE_URL}/forms/events/${event.id}/forms/`,
          {
            withCredentials: true,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          }
        );
      } catch (error) {
        // If 403 or 404, it means no forms exist or no permission
        if (error.response?.status === 403 || error.response?.status === 404) {
          setForms([]);
          return;
        }
        // Re-throw other errors
        throw error;
      }
      // Only try to fetch forms if we haven't already loaded them
      if (forms.length === 0) {
        try {
          const formsResponse = await axios.get(
            `${config.API_BASE_URL}/forms/events/${event.id}/forms/`,
            {
              withCredentials: true,
              headers: {
                'X-Requested-With': 'XMLHttpRequest'
              }
            }
          );

          if (formsResponse?.data) {
            setForms(formsResponse.data);
          }
        } catch (error) {
          // Silently handle form fetching errors
          // console.log('Forms not available or not accessible');
          setForms([]);
        }
      }

      // Only fetch responses if user is the creator
      if (isCreator) {
        await fetchFormResponses();
      }

    } catch (error) {
      console.error('Unexpected error in fetchData:', error);
    } finally {
      setIsLoadingResponses(false);
    }
  }

  // Fetch form responses - only for event creators
  const fetchFormResponses = async () => {
    if (!event?.id || !isCreator) {
      // If not the creator or no event ID, return empty array
      return [];
    }

    setIsLoadingResponses(true);
    try {
      // Fetch all forms for the event (skip HEAD check - just try GET directly)
      const formsResponse = await axios.get(
        `${config.API_BASE_URL}/forms/events/${event.id}/forms/`,
        {
          withCredentials: true,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      
      const formsData = formsResponse.data || [];
      setForms(formsData);
      
      // Fetch form responses for all forms
      const allFormResponses = [];
      
      for (const form of formsData) {
        try {
          const response = await axios.get(
            `${config.API_BASE_URL}/forms/form-responses/?form=${form.id}`,
            {
              withCredentials: true,
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            }
          );
          
          if (response.data && Array.isArray(response.data)) {
            allFormResponses.push(...response.data);
          }
        } catch (responseError) {
          // If we can't get responses for a specific form, continue with others
          // console.log(`Could not fetch responses for form ${form.id}:`, responseError.message);
        }
      }
      
      setFormResponses(allFormResponses);
      
      // Return the collected responses
      return allFormResponses;
    } catch (error) {
      // Silently handle errors
      // console.log('Error loading form responses');
      return [];
    } finally {
      setIsLoadingResponses(false);
    }
  };

  // Handle export of form responses and guest list
  const handleExport = async (format) => {
    try {
      setIsExporting(true);

      // If format is not provided, use a default format
      const exportFormat = format || 'excel';
      // console.log(`Exporting to ${exportFormat}...`);

      // If we're exporting form responses (when a form is selected)
      if (selectedFormId) {
        const token = localStorage.getItem("access_token");
        if (!token) throw new Error("Please log in to export responses");

        const endpoint = `${config.API_BASE_URL}/forms/export-responses/${selectedFormId}/`;

        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          },
          responseType: "blob",
        });

        // Create a blob URL for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `form-responses-${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        // Revoke the blob URL to free up memory
        window.URL.revokeObjectURL(url);

        showSuccessModal(`Exported successfully to Excel`);
        return response;
      } else {
        // Handle guest list export
        // console.log(`Exporting guest list to ${exportFormat}...`);
        showSuccessModal(`Guest list exported successfully to ${exportFormat.toUpperCase()}`);
        return { data: { success: true } };
      }
    } catch (error) {
      // console.error('Export failed:', error);
      showFailureModal(error.response?.data?.message || 'Failed to export. Please try again.');
      throw error;
    } finally {
      setIsExporting(false);
    }
  };



  // Handle export responses
  const handleExportResponses = async (format = 'excel') => {
    try {
      // Find the selected form
      const selectedForm = forms.find(form => form.id.toString() === selectedFormId?.toString());
      if (!selectedForm) {
        showFailureModal('No form selected');
        return;
      }

      // Get responses from the selected form or empty array if none
      const formResponses = selectedForm.responses || [];

      if (formResponses.length === 0) {
        showFailureModal('No responses found to export');
        return;
      }

      // Use event name if available, otherwise fall back to form title
      const eventName = event?.name || 'Event';
      const formTitle = selectedForm.title ? `${eventName}_${selectedForm.title}` : eventName;

      // Process responses to ensure consistent format
      const responses = formResponses.flatMap(response => {
        // If response has nested responses array, use that
        if (response.responses && Array.isArray(response.responses)) {
          return {
            ...response,
            question_responses: response.responses,
            is_complete: true
          };
        }
        return response;
      });

      // Get all unique questions across all responses
      const allQuestions = [];
      responses.forEach(response => {
        const responses = response.question_responses || response.responses || [];
        responses.forEach(item => {
          if (item.question_text && !allQuestions.includes(item.question_text)) {
            allQuestions.push(item.question_text);
          }
        });
      });

      if (format === 'excel') {
        // Prepare worksheet data with questions as columns
        const headerRow = [
          'Respondent',
          'Seat Number',
          'Submitted At',
          ...allQuestions
        ];

        // Prepare data rows
        const dataRows = responses.map(formResponse => {
          const responseMap = {};
          const responses = formResponse.question_responses || formResponse.responses || [];

          // Map each response to its question
          responses.forEach(item => {
            if (item.question_text) {
              responseMap[item.question_text] = item.response || 'No response';
            }
          });

          // Create row with respondent info and responses in the correct order
          return [
            formResponse.user_email || formResponse.email || 'Unknown',
            formResponse.seat_number || 'N/A',
            formResponse.submitted_at ? new Date(formResponse.submitted_at).toLocaleString() : 'N/A',
            ...allQuestions.map(q => responseMap[q] || 'N/A')
          ];
        });

        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

        // Auto-size columns
        const wscols = [
          { wch: 30 }, // Email
          { wch: 15 }, // Seat Number
          { wch: 20 }, // Submitted At
          ...Array(allQuestions.length).fill({ wch: 30 }) // Questions
        ];
        ws['!cols'] = wscols;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Responses');

        // Create a clean filename with event title and date
        const cleanEventTitle = event.title ? event.title.replace(/[^a-z0-9]/gi, '_') : 'Event';
        const dateStr = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `${cleanEventTitle}_responses_${dateStr}.xlsx`);
      } else if (format === 'pdf') {
        // For PDF, create a structured document
        const doc = new jsPDF({
          orientation: 'landscape'
        });

        let startY = 20;

        // Add title and metadata on first page
        doc.setFontSize(18);
        doc.text(event.title || 'Event Responses', 14, startY);
        startY += 10;

        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Form: ${selectedForm.title || 'Untitled Form'}`, 14, startY);
        startY += 8;
        doc.text(`Total Responses: ${responses.length}`, 14, startY);
        startY += 8;
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, startY);
        startY += 15;

        // Prepare table data with questions as columns
        const headerRow = ['Respondent', 'Seat Number', 'Submitted At', ...allQuestions];
        const dataRows = responses.map(formResponse => {
          const responseMap = {};
          const responses = formResponse.question_responses || formResponse.responses || [];

          // Map each response to its question
          responses.forEach(item => {
            if (item.question_text) {
              responseMap[item.question_text] = item.response || 'No response';
            }
          });

          // Create row with respondent info and responses in the correct order
          return [
            formResponse.user_email || formResponse.email || 'Unknown',
            formResponse.seat_number || 'N/A',
            formResponse.submitted_at ? new Date(formResponse.submitted_at).toLocaleString() : 'N/A',
            ...allQuestions.map(q => responseMap[q] || 'N/A')
          ];
        });

        // Add response data as a table using autoTable
        autoTable(doc, {
          head: [headerRow],
          body: dataRows,
          startY: startY,
          styles: {
            fontSize: 8, // Smaller font to fit more columns
            cellPadding: 2,
            overflow: 'linebreak',
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            cellWidth: 'wrap',
            minCellWidth: 20,
            halign: 'left',
            valign: 'middle'
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 7 // Smaller font for header
          },
          columnStyles: {
            0: { cellWidth: 40 }, // Email
            1: { cellWidth: 15 }, // Seat Number
            2: { cellWidth: 30 }, // Date
            // Other columns will be auto-sized
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          margin: { top: 10 },
          tableWidth: 'auto',
          showHead: 'firstPage',
          didDrawPage: function (data) {
            // Reset startY for next page
            startY = 20;
          },
          margin: { top: 10 },
          tableWidth: 'auto',
          showHead: 'firstPage'
        });

        // Save the PDF
        const cleanEventTitle = event.title ? event.title.replace(/[^a-z0-9]/gi, '_') : 'Event';
        const dateStr = new Date().toISOString().split('T')[0];
        doc.save(`${cleanEventTitle}_responses_${dateStr}.pdf`);
      }
    } catch (error) {
      // console.error('Export error:', error);
      showFailureModal(`Failed to export responses: ${error.message}`);
    }
  };

  // Load forms and responses when component mounts or event changes
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!event?.id) return;

      try {
        await Promise.all([
          fetchForms(),
          fetchData()
        ]);
      } catch (error) {
        // console.error('Error loading data:', error);
        if (isMounted) {
          showFailureModal("Failed to load form data. Please try again.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
      setLoading(false);
    };
  }, [event?.id]);

  // Constants
  const itemsPerPage = 10;

  // Form Responses Section
  const renderQuestionResponse = (questionResponse) => {
    if (!questionResponse) return null;

    let answer = questionResponse.answer;
    // Format answer based on question type
    if (Array.isArray(answer)) {
      answer = answer.join(', ');
    } else if (typeof answer === 'object' && answer !== null) {
      answer = JSON.stringify(answer);
    }

    return (
      <div key={questionResponse.id} className="mb-4 p-3 border-b border-gray-100">
        <h4 className="font-medium text-gray-700">{questionResponse.question_text || 'Question'}</h4>
        <p className="text-gray-600">{answer || 'No answer provided'}</p>
      </div>
    );
  };

  const renderFormResponseDetails = (response) => {
    if (!response) return null;

    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium">
            {response.user?.name || response.invitation?.name || 'Guest'}
            <span className="text-sm text-gray-500 block">
              {response.user?.email || response.invitation?.email || 'No email'}
            </span>
          </h4>
          <span className="text-sm text-gray-500">
            {new Date(response.submitted_at).toLocaleString()}
          </span>
        </div>

        <div className="bg-white p-4 rounded border border-gray-200">
          {response.question_responses?.length > 0 ? (
            response.question_responses.map(renderQuestionResponse)
          ) : (
            <p className="text-gray-500 italic">No answers provided</p>
          )}
        </div>
      </div>
    );
  };

  const renderFormResponses = () => {
    // Find the form that matches the selectedFormId
    const selectedForm = forms.find(form => form.id.toString() === selectedFormId?.toString());

    // Get responses from the selected form or empty array if none
    const formResponses = selectedForm?.responses || [];

    // Process responses to ensure consistent format
    let responses = [];
    if (Array.isArray(formResponses)) {
      responses = formResponses.flatMap(response => {
        // If response has nested responses array, use that
        if (response.responses && Array.isArray(response.responses)) {
          return {
            ...response,
            question_responses: response.responses,
            is_complete: true
          };
        }
        // Otherwise use the response as is
        return response;
      });
    }

    const responseCount = responses.length;
    const totalForms = forms.length;

    // Debug information
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('Forms data:', forms);
    //   console.log('Selected form ID:', selectedFormId);
    //   console.log('Selected form:', selectedForm);
    //   console.log('Raw form responses:', formResponses);
    //   console.log('Processed responses:', responses);
    // }

    // return (
    //   <div className="bg-white rounded-lg shadow p-6 mb-6">
    //     <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
    //       <div>
    //         <h3 className="text-xl font-semibold">Form Responses</h3>
    //         {selectedForm ? (
    //           <div className="flex items-center mt-1 text-sm text-gray-600">
    //             <span className="font-medium">{selectedForm.title}</span>
    //             <span className="mx-2">•</span>
    //             <span>{responseCount} {responseCount === 1 ? 'response' : 'responses'}</span>
    //           </div>
    //         ) : (
    //           <p className="text-sm text-gray-500 mt-1">No form selected</p>
    //         )}
    //       </div>

    //       {forms.length > 0 && (
    //         <div className="flex-1 md:max-w-xs">
    //           <select
    //             value={selectedFormId || ''}
    //             onChange={(e) => setSelectedFormId(e.target.value)}
    //             className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    //           >
    //             <option value="">Select a form</option>
    //             {forms.map((form) => (
    //               <option key={form.id} value={form.id}>
    //                 {form.title} ({form.response_count || 0} {form.response_count === 1 ? 'response' : 'responses'})
    //               </option>
    //             ))}
    //           </select>
    //         </div>
    //       )}
    //     </div>

    //     {/* Debug information - only shown in development */}
    //     {process.env.NODE_ENV === 'development' && (
    //       <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-600 mb-6 border border-gray-200">
    //         <div className="font-medium mb-2">Debug Information</div>
    //         <div className="grid grid-cols-2 gap-2">
    //           <div>Total Forms: <span className="font-mono">{totalForms}</span></div>
    //           <div>Selected Form ID: <span className="font-mono">{selectedFormId || 'None'}</span></div>
    //           <div>Responses Found: <span className="font-mono">{responseCount}</span></div>
    //           <div>Selected Form: <span className="font-mono">{selectedForm?.title || 'None'}</span></div>
    //         </div>
    //         {selectedForm && (
    //           <div className="mt-3">
    //             <div className="font-medium mb-1">Form Details:</div>
    //             <pre className="text-xs overflow-auto p-2 bg-gray-100 rounded max-h-40">
    //               {JSON.stringify(selectedForm, null, 2)}
    //             </pre>
    //           </div>
    //         )}
    //       </div>
    //     )}

    //     {!selectedFormId ? (
    //       <div className="text-center py-8 text-gray-500">
    //         <p>Please select a form to view responses</p>
    //       </div>
    //     ) : responses.length === 0 ? (
    //       <div className="text-center py-8 text-gray-500">
    //         <p>No responses found for this form</p>
    //       </div>
    //     ) : (
    //       <div className="space-y-4">
    //         {responses.map((response, index) => {
    //           // Use question_responses if available, otherwise fall back to responses
    //           const questionResponses = response.question_responses || response.responses || [];
    //           return (
    //             <div key={response.id || index} className="border rounded-lg overflow-hidden">
    //               <div className="p-4 bg-gray-50 border-b">
    //                 <div className="flex justify-between items-center">
    //                   <div>
    //                     <h4 className="font-medium">
    //                       {response.user_email || response.email || 'Guest'}
    //                     </h4>
    //                     <p className="text-sm text-gray-500">
    //                       {response.submitted_at && (
    //                         <span>Submitted: {new Date(response.submitted_at).toLocaleString()}</span>
    //                       )}
    //                     </p>
    //                   </div>
    //                   <span className={`px-2 py-1 text-xs rounded-full ${response.is_complete ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
    //                     }`}>
    //                     {response.is_complete ? 'Complete' : 'Incomplete'}
    //                   </span>
    //                 </div>
    //               </div>

    //               {questionResponses.length > 0 && (
    //                 <div className="p-4">
    //                   <div className="space-y-3">
    //                     {questionResponses.map((qr, qrIndex) => (
    //                       <div key={qrIndex} className="text-sm">
    //                         <p className="font-medium text-gray-700">{qr.question_text || 'Question'}</p>
    //                         <p className="text-gray-600">{qr.response || 'No response'}</p>
    //                       </div>
    //                     ))}
    //                   </div>
    //                 </div>
    //               )}
    //             </div>
    //           );
    //         })}
    //       </div>
    //     )}
    //   </div>
    // );
  };



  // This will be set after filteredInvitations is defined

  // Load data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchData();
        await fetchFormResponses();
      } catch (error) {
        // console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [event]);

  // Fetch form submissions when form is selected
  useEffect(() => {
    const processData = () => {
      // console.log("Processing invitations:", invitations)
      // console.log("Processing guests:", guests)

      // Process invitations
      if (invitations && Array.isArray(invitations)) {
        setInvitations(invitations)
      } else if (invitations && invitations.results) {
        setInvitations(invitations.results)
        setPagination((prev) => ({
          ...prev,
          invitations: {
            count: invitations.count || 0,
            next: invitations.next,
            previous: invitations.previous,
          },
        }))
      } else {
        setInvitations([])
      }

      // Process guests - handle both direct guest objects and nested guest.guest objects
      const processGuest = (guest) => {
        if (!guest) return null
        // If guest has a nested guest object, use that
        if (guest.guest) {
          return {
            ...guest,
            email: guest.guest.email,
            name: guest.guest.name || `${guest.guest.first_name || ""} ${guest.guest.last_name || ""}`.trim(),
          }
        }
        // Otherwise use the guest object directly
        return {
          ...guest,
          email: guest.email,
          name: guest.name || `${guest.first_name || ""} ${guest.last_name || ""}`.trim(),
        }
      }

      if (guests) {
        let guestsToProcess = []

        // Handle both array and paginated response
        if (Array.isArray(guests)) {
          guestsToProcess = guests.map(processGuest).filter(Boolean)
        } else if (guests.results && Array.isArray(guests.results)) {
          guestsToProcess = guests.results.map(processGuest).filter(Boolean)
          setPagination((prev) => ({
            ...prev,
            guests: {
              count: guests.count || 0,
              next: guests.next,
              previous: guests.previous,
            },
          }))
        }

        // console.log("Processed guests:", guestsToProcess)
        setGuests(guestsToProcess)
      } else {
        setGuests([])
      }
    }

    processData()
  }, [invitations, guests])

  const invitationItemsPerPage = 10

  // Combine and filter both invitations and guests with search and status filtering
  const filteredInvitations = useMemo(() => {
    // console.log("Filtering invitations and guests...")

    // Use the already processed state
    const safeInvitations = Array.isArray(invitationsState) ? invitationsState : []
    const safeGuests = Array.isArray(guestsState) ? guestsState : []

    // Use the already processed state

    // For non-creator users, only show their own invitation (excluding withdrawn ones)

    // For users with no valid user data or creators, show appropriate content

    // For creators, show all invitations
    if (safeInvitations.length === 0 && safeGuests.length === 0) {
      // console.log("No invitations or guests to process")
      return []
    }

    // Create a map to store guests by ID and email for quick lookup
    const guestMap = new Map()
    safeGuests.forEach((guest) => {
      // Handle different guest data structures
      const guestObj = typeof guest.guest === 'object' ? guest.guest : { id: guest.guest };
      const email = guestObj?.email || guest?.email;
      const guestWithEmail = {
        ...guest,
        guest: {
          ...guestObj,
          id: guestObj.id || guest.guest,
          email: email,
          name: guest.guest_name || guestObj?.name || ''
        },
        // Ensure we have an email for lookup
        email: email
      };

      if (guestObj.id) {
        guestMap.set(`id_${guestObj.id}`, guestWithEmail);
      }
      if (email) {
        guestMap.set(email.toLowerCase(), guestWithEmail);
      } else {
        // console.log("Guest with missing email:", guest);
      }
    })

    // Process invitations first
    const processedEmails = new Set()
    const result = []

    // Process each invitation
    safeInvitations.forEach((invitation) => {
      if (!invitation) return

      const email = (invitation.recipient_email || "").toLowerCase()
      const recipientId = invitation.recipient?.id || invitation.recipient;

      // Skip if no email or recipient ID
      if (!email && !recipientId) return;

      // Create a unique key for this invitation
      const inviteKey = email || `id_${recipientId}`;

      // Skip if we've already processed this invitation
      if (processedEmails.has(inviteKey)) {
        // console.log("Skipping duplicate invitation for:", inviteKey);
        return;
      }
      processedEmails.add(inviteKey);

      // Find corresponding guest data if it exists - try both email and ID
      const guestData = guestMap.get(email) ||
        (recipientId ? guestMap.get(`id_${recipientId}`) : null);

      // Create the invitation object with merged data
      const invitationWithGuest = {
        ...invitation,
        key: `inv_${invitation.id || inviteKey}`,
        // Include seat_number at the root level for easier access in the table
        seat_number: guestData?.seat_number || invitation.seat_number || null,
        guest: guestData
          ? {
            ...(typeof guestData.guest === 'object' ? guestData.guest : { id: guestData.guest }),
            seat_number: guestData.seat_number,
            added_at: guestData.added_at,
            email: guestData.guest?.email || guestData.email,
            name: guestData.guest_name || guestData.guest?.name
          }
          : null,
        // Ensure we have all necessary fields with fallbacks
        recipient: invitation.recipient || {
          id: guestData?.guest?.id,
          email: email,
          first_name: invitation.recipient?.first_name || "",
          last_name: invitation.recipient?.last_name || "",
        },
        recipient_email: email,
        status: invitation.status || "pending",
        created_at: invitation.created_at || new Date().toISOString(),
      }

      result.push(invitationWithGuest)
    })

    // Process guests that don't have invitations
    safeGuests.forEach((guest) => {
      if (!guest?.guest?.email) return

      const email = guest.guest.email.toLowerCase()
      if (processedEmails.has(email)) return // Skip if already processed

      // Create a guest-only entry
      result.push({
        id: `guest_${guest.id}`,
        key: `guest_${guest.id}`,
        // Include seat_number at the root level for easier access in the table
        seat_number: guest.seat_number || null,
        guest: {
          ...guest.guest,
          seat_number: guest.seat_number,
          added_at: guest.added_at,
        },
        recipient: {
          id: guest.guest.id,
          email: email,
          first_name: guest.guest.first_name || "",
          last_name: guest.guest.last_name || "",
        },
        recipient_email: email,
        status: "accepted", // Guest records imply acceptance
        created_at: guest.added_at || new Date().toISOString(),
        isGuestOnly: true,
      })
    })

    // Apply search filter if searchQuery exists
    const searchLower = (invitationSearch || "").toLowerCase()
    const filtered = searchLower
      ? result.filter((item) => {
        return (
          item.recipient?.first_name?.toLowerCase().includes(searchLower) ||
          item.recipient?.last_name?.toLowerCase().includes(searchLower) ||
          item.recipient_email?.toLowerCase().includes(searchLower) ||
          item.guest?.seat_number?.toString().includes(searchLower)
        )
      })
      : result

    // Apply status filter if not showing all
    return statusFilter === "all" ? filtered : filtered.filter((item) => item.status === statusFilter)
  }, [invitationsState, guestsState, invitationSearch, statusFilter])

  // Fetch form invitation statuses for the selected form
  const fetchFormInvitationStatuses = async () => {
    if (!selectedFormId) return;

    // console.log('🔍 Fetching form invitation statuses for form:', selectedFormId);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const response = await axios.get(
        `${config.API_BASE_URL}/forms/form-invitations/?form=${selectedFormId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // console.log('🔍 Form invitations response:', response.data);

      // Create a map of email to form invitation status
      const formStatusMap = {};
      response.data.results.forEach(invitation => {
        // console.log('🔍 Individual form invitation:', invitation);
        formStatusMap[invitation.email] = {
          status: invitation.status,
          sent_at: invitation.sent_at,
          opened_at: invitation.opened_at,
          submitted_at: invitation.submitted_at,
        };
      });

      // console.log('🔍 Form status map:', formStatusMap);

      // Update invitations with form status
      setInvitations(prevInvitations => {
        // console.log('🔍 Previous invitations:', prevInvitations);
        const updatedInvitations = prevInvitations.map(invitation => {
          const email = invitation.recipient_email || invitation.recipient?.email;
          const formStatus = formStatusMap[email];
          
          // console.log('🔍 Processing invitation:', email, 'Form status:', formStatus);
          
          return {
            ...invitation,
            form_status: formStatus ? formStatus.status : 'not_sent',
            form_sent_at: formStatus ? formStatus.sent_at : null,
          };
        });
        
        // console.log('🔍 Updated invitations:', updatedInvitations);
        return updatedInvitations;
      });
    } catch (error) {
      console.error('🔍 Error fetching form invitation statuses:', error);
    }
  };

  // Update response count when forms or selected form changes
  useEffect(() => {
    const updateResponseCount = () => {
      // If we have forms but no selected form, try to find the first form for this event
      if (forms.length > 0 && !selectedFormId) {
        const firstForm = forms[0];
        // console.log(`Auto-selecting first form for event:`, firstForm);
        setSelectedFormId(firstForm.id.toString());
        return;
      }

      if (!selectedFormId) {
        // console.log('No form available for this event yet');
        setResponseCount(0);
        return;
      }

      // Find the selected form
      const form = forms.find(f => f.id.toString() === selectedFormId.toString());

      if (form) {
        const count = form.response_count || 0;
        // console.log(`Updating response count for form ${form.id} to ${count}`);
        setResponseCount(count);

        // Update the form status in the invitations based on actual responses
        if (form?.responses?.length > 0) {
          // Create a Set of respondent emails for quick lookup
          const respondentEmails = new Set(
            form.responses
              .filter(r => r.user_email)
              .map(r => r.user_email.toLowerCase())
          );

          setInvitations(prevInvitations =>
            prevInvitations.map(invite => {
              const inviteEmail = (invite.recipient_email || '').toLowerCase();
              const hasResponded = respondentEmails.has(inviteEmail);

              return {
                ...invite,
                form_status: hasResponded
                  ? 'completed'
                  : (invite.form_status || 'not_sent')
              };
            })
          );
        } else {
          // If no responses, ensure we don't mark anything as completed
          setInvitations(prevInvitations =>
            prevInvitations.map(invite => ({
              ...invite,
              form_status: invite.form_status || 'not_sent'
            }))
          );
        }
      } else {
        // console.log(`Form ${selectedFormId} not found in forms list`);
        setResponseCount(0);
      }
    };

    updateResponseCount();
  }, [selectedFormId, forms]);

  useEffect(() => {
    // console.log('🔍 useEffect triggered - selectedFormId:', selectedFormId);
    fetchFormInvitationStatuses();
  }, [selectedFormId]);

  // Response count is handled by the earlier useEffect

  // Calculate pagination
  const invitationIndexOfLastItem = invitationPage * invitationItemsPerPage
  const invitationIndexOfFirstItem = Math.max(0, invitationIndexOfLastItem - invitationItemsPerPage)
  const invitationCurrentItems = filteredInvitations.slice(invitationIndexOfFirstItem, invitationIndexOfLastItem)
  const invitationTotalPages = Math.ceil(filteredInvitations.length / invitationItemsPerPage) || 1

  // Status badge component
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Pending", bg: "bg-yellow-100", text: "text-yellow-800" },
      accepted: { label: "Accepted", bg: "bg-green-100", text: "text-green-800" },
      rejected: { label: "Rejected", bg: "bg-red-100", text: "text-red-800" },
      withdrawn: { label: "Withdrawn", bg: "bg-gray-100", text: "text-gray-800" },
    }

    const config = statusConfig[status] || statusConfig.pending
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>{config.label}</span>
    )
  }

  const isRecipient = (invitation) => {
    if (!user?.id) return false

    // Convert all IDs to strings for consistent comparison
    const userId = String(user.id)
    const recipientId = String(invitation.recipient || "")
    const senderId = String(invitation.sender || "")

    // Check if user is the recipient (for creator-initiated invitations)
    const isDirectRecipient = recipientId === userId

    // Check if user is the sender (for guest-initiated requests)
    const isSender = senderId === userId

    // Also check by email as a fallback
    const emailMatches = invitation.recipient_email?.toLowerCase() === user.email?.toLowerCase()

    // For guest-initiated requests, the user is both sender and recipient
    // So we should show it if either they are the sender or recipient
    return isDirectRecipient || isSender || emailMatches
  }

  // Handle guest registration request
  const handleRequestToAttend = async () => {
    if (!event?.id) {
      showFailureModal("Event ID missing.")
      return
    }

    const token = localStorage.getItem("access_token")
    if (!token) {
      showFailureModal("Authentication token missing. Please log in.")
      return
    }

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/invitations/register-to-attend/`,
        {
          event: event.id,
          status: "pending",
          recipient: user?.id,
          recipient_email: user?.email,
          message: `Request to attend ${event.title}`,
          initiated_by: "guest",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.status === 201) {
        await fetchInvitationsAndGuests()
        showSuccessModal("Request to attend sent successfully!")
      } else {
        showFailureModal("Failed to send request. Please try again.")
      }
    } catch (error) {
      // console.error("Error requesting to attend:", error)
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An error occurred while processing your request."
      showFailureModal(errorMessage)
    }
  }

  // Helper function to get CSRF token from cookies
  const getCsrfToken = () => {
    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrftoken="))
      ?.split("=")[1]
    return cookieValue || ""
  }

  // Handle sending form to a specific guest or multiple guests and handle form submission
  const handleSendForm = async (selectedGuests = null) => {
    console.log('🔍 handleSendForm called, selectedFormId:', selectedFormId);
    console.log('🔍 forms.length:', forms.length);
    
    if (!selectedFormId) {
      console.log('🔍 No form selected, opening form builder modal');
      console.log('🔍 showFormBuilder before:', showFormBuilder);
      // Open the form builder modal to create a form
      setShowFormBuilder(true);
      console.log('🔍 showFormBuilder after setting to true:', showFormBuilder);
      return;
    }

    setError(null)
    setLoading(true)
    setIsSendingForm(true)

    try {
      const token = localStorage.getItem("access_token")
      // console.log('Retrieved token from localStorage:', token ? 'Token exists' : 'No token found')
      if (!token) {
        throw new Error("Please log in to send forms")
      }

      // Get the list of invitations to process
      let invitationsToProcess = []

      // console.log('Selected guests:', selectedGuests)
      // console.log('Selected invitations state:', selectedInvitations)
      // console.log('Selected invitation:', selectedInvitation)

      if (Array.isArray(selectedGuests)) {
        // If an array of guests was passed directly
        invitationsToProcess = selectedGuests.flat()
      } else if (selectedInvitation) {
        // If we have a single selected invitation
        invitationsToProcess = [selectedInvitation]
      } else if (Object.keys(selectedInvitations).length > 0) {
        // If we have selected invitations from the table
        invitationsToProcess = filteredInvitations.filter(inv => selectedInvitations[inv.id])
      } else {
        throw new Error("No guests selected to send the form to")
      }

      // console.log('Invitations to process:', invitationsToProcess)

      if (invitationsToProcess.length === 0) {
        throw new Error("No valid guests selected to send the form to")
      }

      // Get the selected form details
      const selectedForm = forms.find(f => f.id.toString() === selectedFormId.toString())
      if (!selectedForm) {
        throw new Error("Selected form not found")
      }

      // Send form to each selected guest
      const sendPromises = invitationsToProcess.map(async (invitation) => {
        const email = invitation.recipient_email || invitation.recipient?.email || invitation.guest?.email
        if (!email) {
          // console.warn('Skipping invitation with no email:', invitation)
          return null
        }

        try {
          // console.log(`Sending form ${selectedFormId} to ${email}...`)

          // Create form invitation directly (no need to check existing)
          console.log('🔍 Event object:', event)
          console.log('🔍 Event date:', event.date)
          
          // Ensure we have a valid datetime for expires_at
          let expiresAt
          try {
            expiresAt = new Date(event.date + 'T23:59:59').toISOString()
            console.log('🔍 Converted expires_at:', expiresAt)
          } catch (dateError) {
            console.log('🔍 Date conversion error:', dateError)
            expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours from now as fallback
          }
          
          const response = await axios.post(
            `${config.API_BASE_URL}/forms/form-invitations/`,
            {
              email: email,
              form: selectedFormId,
              event: event.id,
              expires_at: expiresAt
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-CSRFToken': getCsrfToken()
              },
            }
          )

          // Update invitation status to sent
          if (response.data && response.data.id) {
            await axios.patch(
              `${config.API_BASE_URL}/forms/form-invitations/${response.data.id}/`,
              {
                status: 'sent',
                sent_at: new Date().toISOString(),
                expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              }
            )
          }

          return response.data
        } catch (error) {
          // console.error(`Error sending form to ${email}:`, error)
          // Return the error for this specific invitation
          return {
            error: true,
            email,
            message: error.response?.data?.message || error.message || 'Failed to send form'
          }
        }
      })

      const results = await Promise.all(sendPromises)
      const successfulSends = results.filter(result => result && !result.error)
      const failedSends = results.filter(result => result && result.error)

      // Show success message for successful sends
      if (successfulSends.length > 0) {
        showSuccessModal(
          `Form sent successfully to ${successfulSends.length} ${successfulSends.length === 1 ? 'recipient' : 'recipients'}`
        )
      }

      // Show error message for failed sends
      if (failedSends.length > 0) {
        const errorMessage = `Failed to send form to ${failedSends.length} ${failedSends.length === 1 ? 'recipient' : 'recipients'}. ` +
          failedSends.map(f => `${f.email}: ${f.message}`).join('; ')
        showFailureModal(errorMessage)
      }

      // Refresh the invitations list
      await fetchInvitationsAndGuests()

      // Close the modal if all sends were successful
      if (failedSends.length === 0) {
        setShowSendFormModal(false)
      }
    } catch (error) {
      // console.error('Error in handleSendForm:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send form'
      showFailureModal(errorMessage)
    } finally {
      setLoading(false)
      setIsSendingForm(false)
    }
  }

  // Toggle selection for a single invitation
  const toggleInvitationSelection = (invitationId) => {
    setSelectedInvitations(prev => ({
      ...prev,
      [invitationId]: !prev[invitationId]
    }));
  };

  // Toggle select all invitations
  const toggleSelectAll = () => {
    if (Object.keys(selectedInvitations).length === filteredInvitations.length) {
      setSelectedInvitations({});
    } else {
      const allSelected = {};
      filteredInvitations.forEach(inv => {
        allSelected[inv.id] = true;
      });
      setSelectedInvitations(allSelected);
    }
  };

  // Handle seat assignment
  const handleAssignSeat = (invitation) => {
    setSelectedGuest(invitation);
    setShowSeatAssignment(true);
  };

  // Handle delete invitation - shows confirmation dialog
  const handleDeleteInvitation = (invitation) => {
    setSelectedInvitation(invitation);
    setShowDeleteConfirm(true);
  };

  // Handle responding to an invitation (Accept/Reject/Withdraw)
  const handleRespondToInvitation = async (invitationId, action) => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      showFailureModal("Authentication token missing. Please log in.");
      return false;
    }

    try {
      const baseUrl = config.API_BASE_URL.replace(/\/+$/, "");

      // First, find the invitation to get its details
      const invitation = filteredInvitations.find(inv => inv.id === invitationId);
      if (!invitation) {
        throw new Error('Invitation not found');
      }

      // Debug logging
      console.log('Withdraw request - Invitation ID:', invitationId);
      console.log('Found invitation:', invitation);
      console.log('All invitations:', invitations);

      // Get CSRF token from cookies
      const csrftoken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];

      let response;

      if (action === 'withdraw') {
        // For guest-initiated requests, we need to delete the invitation instead of withdraw acceptance
        const isGuestInitiated = invitation.initiated_by === 'guest';
        console.log('Is guest initiated:', isGuestInitiated);
        
        // Use the actual invitation ID from the found invitation
        const actualInvitationId = invitation.id;
        
        if (isGuestInitiated) {
          // For guest-initiated requests, use the delete-invitation endpoint
          response = await axios.delete(
            `${config.API_BASE_URL}/invitations/delete-invitation/${actualInvitationId}/`,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            }
          );
        } else {
          // Use the withdrawal endpoint for withdrawing responses (creator-initiated)
          response = await axios.post(
            `${baseUrl}/invitations/withdraw-acceptance/`,
            { invitation_id: actualInvitationId },
            {
              withCredentials: true,
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrftoken,
                'X-Requested-With': 'XMLHttpRequest',
                'Authorization': `Bearer ${token}`
              }
            }
          );
        }
      } else {
        // Use the regular respond endpoint for accept/reject
        response = await axios.post(
          `${baseUrl}/invitations/invite/respond/${invitationId}/`,
          { action },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrftoken,
              'X-Requested-With': 'XMLHttpRequest',
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }

      console.log('Response from server:', response.data);

      console.log('Response from server:', response.data);

      // Update the local state to reflect the change
      setInvitations(prevInvitations =>
        prevInvitations.map(inv =>
          inv.id === invitationId
            ? {
              ...inv,
              status: action === 'withdraw'
                ? 'withdrawn'  // Fixed: Use 'withdrawn' instead of 'pending'
                : action === 'accept'
                  ? 'accepted'
                  : 'rejected'
            }
            : inv
        )
      );

      // Show success message
      const successMessage = action === 'accept'
        ? 'Invitation accepted successfully!'
        : action === 'reject'
          ? 'Invitation declined successfully.'
          : 'Response withdrawn successfully.';

      setSuccessMessage(successMessage);
      setSuccessModal({ open: true, message: successMessage });
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

      return true;

    } catch (error) {
      console.error('Error responding to invitation:', error);

      // Handle 403 Forbidden error more gracefully
      if (error.response?.status === 403) {
        console.log('403 Forbidden - User may not have permission or not logged in');
        showFailureModal('You do not have permission to perform this action.');
        return false;
      }

      const errorMessage = error.response?.data?.error ||
        error.response?.data?.detail ||
        'An error occurred while processing your request.';
      showFailureModal(errorMessage);
      return false;
    }
  };

  // Handle delete invitation confirmation
  const confirmDeleteInvitation = async () => {
    if (!selectedInvitation) return;

    const invitationId = selectedInvitation.id || selectedInvitation;
    if (!invitationId) return;

    const token = localStorage.getItem("access_token");
    if (!token) {
      showFailureModal("Authentication token missing. Please log in.");
      return;
    }

    setIsDeleting(true);
    try {
      const baseUrl = config.API_BASE_URL.replace(/\/+$/, "");
      await axios.delete(
        `${baseUrl}/invitations/${invitationId}/`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Refresh the list
      await fetchInvitationsAndGuests();
      setSuccessMessage('Invitation deleted successfully');
      setSuccessModal({ open: true, message: 'Invitation deleted successfully' });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to delete invitation. Please try again.';
      setErrorMessage(errorMsg);
      setFailureModal({ open: true, message: errorMsg });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setSelectedInvitation(null);
    }
  };

  // Render the component
  return (
    <div className="space-y-6">
      {/* Guest List Table */}
      <GuestListTable
        invitations={filteredInvitations}
        isCreator={isCreator}
        selectedInvitations={selectedInvitations}
        toggleInvitationSelection={toggleInvitationSelection}
        toggleSelectAll={toggleSelectAll}
        onSendForm={handleSendForm}
        onManageForms={handleManageForms}
        onRespondToInvitation={handleRespondToInvitation}
        onAssignSeat={handleAssignSeat}
        onDelete={handleDeleteInvitation}
        onViewResponse={handleViewResponse}
        onExport={handleExport}
        onAddGuest={() => setShowAddGuestForm(true)}
        onRequestToAttend={handleRequestToAttend}
        currentUserEmail={user?.email}
        currentUser={user}
        isExporting={isExporting}
        selectedFormId={selectedFormId}
        responseCount={responseCount}
        event={event}
        forms={forms}
      />
      {/* Form Responses Section */}
      {/* {isCreator && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Form Responses</h2>
            {formResponses.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDown size={16} />}
                  onClick={() => handleExportResponses('excel')}
                  className="text-green-600 border-green-600 hover:bg-green-50"
                >
                  Export Excel
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileText size={16} />}
                  onClick={() => handleExportResponses('pdf')}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  Export PDF
                </Button>
              </div>
            )}
          </div>
          {renderFormResponses()}
        </div>
      )} */}

      {/* Add Guest Modal */}
      <AddGuestModal
        isOpen={showAddGuestForm}
        onClose={() => setShowAddGuestForm(false)}
        event={event}
        fetchInvitationsAndGuests={fetchInvitationsAndGuests}
        showSuccessModal={showSuccessModal}
        showFailureModal={showFailureModal}
      />

      {/* Seat Assignment Modal */}
      <SeatAssignmentModal
        isOpen={showSeatAssignment && !!selectedGuest}
        onClose={() => {
          setShowSeatAssignment(false);
          setSelectedGuest(null);
        }}
        seatNumber={selectedGuest?.seat_number || ''}
        setSeatNumber={(value) => {
          setSelectedGuest(prev => ({
            ...prev,
            seat_number: value
          }));
        }}
        onAssign={async (seatNumber) => {
          if (!selectedGuest) return;

          const token = localStorage.getItem("access_token");
          if (!token) {
            showFailureModal("Authentication token missing. Please log in.");
            return;
          }

          try {
            console.log('🔍 Selected Guest Object:', selectedGuest);
            console.log('🔍 Event ID:', event.id);
            console.log('🔍 Seat Number:', seatNumber);

            // The recipient field directly contains the user ID
            const userId = selectedGuest.recipient;

            console.log('🔍 Using recipient ID:', userId);

            if (!userId) {
              console.error('❌ Recipient ID is missing in the selectedGuest object');
              throw new Error('Could not find recipient ID for this invitation');
            }

            const response = await axios.patch(
              `${config.API_BASE_URL}/invitations/guests/${userId}/assign-seat/`,
              {
                seat_number: seatNumber,
                event_id: event.id
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                }
              }
            );

            await fetchInvitationsAndGuests();
            showSuccessModal('Seat assigned successfully');
            return true;
          } catch (error) {
            // console.error('Error assigning seat:', error);
            const errorMessage = error.response?.data?.message || 'Failed to assign seat';
            showFailureModal(errorMessage);
            return false;
          }
        }}
      />

      {/* Response Viewer Modal */}
      <ResponseViewerModal
        open={isResponseModalOpen}
        onClose={handleCloseResponseViewer}
        response={viewingResponse}
        isLoading={isLoadingResponse}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedInvitation(null);
        }}
        onConfirm={async () => {
          if (!selectedInvitation) return;

          const invitationId = selectedInvitation.id || selectedInvitation;
          if (!invitationId) return;

          const token = localStorage.getItem("access_token");
          if (!token) {
            showFailureModal("Authentication token missing. Please log in.");
            return;
          }

          setIsDeleting(true);
          try {
            const baseUrl = config.API_BASE_URL.replace(/\/+$/, "");
            await axios.delete(
              `${baseUrl}/invitations/${invitationId}/`,
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                }
              }
            );

            await fetchInvitationsAndGuests();
            showSuccessModal('Invitation deleted successfully');
          } catch (error) {
            // console.error('Error deleting invitation:', error);
            const errorMessage = error.response?.data?.message || 'Failed to delete invitation';
            showFailureModal(errorMessage);
          } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
            setSelectedInvitation(null);
          }
        }}
        title="Delete Invitation"
        content="Are you sure you want to delete this invitation? This action cannot be undone."
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        confirmColor="error"
        loading={isDeleting}
      />

      {/* Send Form Modal */}
      {showSendFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Send Form</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Form
              </label>
              <div className="flex gap-2">
                <select
                  className="flex-1 p-2 border border-gray-300 rounded"
                  value={selectedFormId || ''}
                  onChange={(e) => setSelectedFormId(e.target.value)}
                >
                  <option value="">Select a form</option>
                  {forms.map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.title}
                    </option>
                  ))}
                </select>
                {selectedFormId && (
                  <>
                    <button
                      type="button"
                      className="px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                      onClick={() => handleViewForm(selectedFormId)}
                      title="View Form"
                    >
                      <FileText size={16} />
                    </button>
                    <button
                      type="button"
                      className="px-3 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                      onClick={() => handleEditForm(selectedFormId)}
                      title="Edit Form"
                    >
                      <Edit3 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
                onClick={() => setShowSendFormModal(false)}
                disabled={isSendingForm}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                onClick={handleSendForm}
                disabled={!selectedFormId || isSendingForm}
              >
                {isSendingForm ? 'Sending...' : 'Send Form'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons - Commented out as per request
      {isCreator && (
        <div className="fixed bottom-6 right-6 flex space-x-4">
          <button
            onClick={() => setShowAddGuestForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg flex items-center justify-center"
            title="Add Guest"
          >
            <UserPlus size={24} />
          </button>
          <button
            onClick={() => setShowSendFormModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white rounded-full p-3 shadow-lg flex items-center justify-center"
            title="Send Form"
          >
            <Send size={24} />
          </button>
        </div>
      )}
      */}

      {/* Success/Failure Modals */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg flex items-center space-x-4">
          <span>{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-white hover:text-gray-200 focus:outline-none"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg">
          {error}
        </div>
      )}

      {/* Test Modal - Simple Debug */}
      {showFormBuilder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Test Modal</h2>
            <p>Form builder modal should open here!</p>
            <button
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
              onClick={() => setShowFormBuilder(false)}
            >
              Close Test Modal
            </button>
          </div>
        </div>
      )}

      {/* Simple Form Builder Modal */}
      {showFormBuilder && (
        <>
          {console.log('🔍 Rendering SimpleFormBuilder modal, showFormBuilder:', showFormBuilder)}
          <SimpleFormBuilder
            open={showFormBuilder}
            onClose={() => setShowFormBuilder(false)}
            eventId={event.id}
            formId={selectedFormId}
            onFormCreated={handleFormCreated}
          />
          {console.log('🔍 SimpleFormBuilder props:', { open: showFormBuilder, eventId: event.id, formId: selectedFormId })}
        </>
      )}

      {/* View Form Modal */}
      {showViewFormModal && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{selectedForm.title || 'Form Preview'}</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setShowViewFormModal(false)}
              >
                <X size={24} />
              </button>
            </div>
            
            {selectedForm.description && (
              <p className="text-gray-600 mb-6">{selectedForm.description}</p>
            )}

            <div className="space-y-4">
              {selectedForm.questions && selectedForm.questions.length > 0 ? (
                selectedForm.questions.map((question, index) => (
                  <div key={question.id || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">
                          {question.label}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </h3>
                        {question.help_text && (
                          <p className="text-sm text-gray-500 mt-1">{question.help_text}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Display field type and options */}
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Type:</span> {question.field?.field_type || 'text'}
                      {question.field?.options && question.field.options.length > 0 && (
                        <div className="mt-1">
                          <span className="font-medium">Options:</span>
                          <ul className="list-disc list-inside ml-4 mt-1">
                            {question.field.options.map((option, optIndex) => (
                              <li key={optIndex}>{option}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No questions in this form</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GuestListTab